<template>
  <div :class="containerClass">
    <page-header
      :title="$route.params.id ? 'Edit Pathway' : 'Add New Pathway'"
      :container-class="null"
      class="mb-16pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input
          id="title"
          placeholder="e.g. CNA → RN Pathway"
          v-model="form.title"
          required
        />
      </b-form-group>

      <b-form-group label="Subtitle" label-for="subtitle" label-class="form-label" description="One short line under the title, e.g. Community college ADN pathway.">
        <b-form-input id="subtitle" placeholder="e.g. Community college ADN pathway" v-model.trim="form.subtitle" />
      </b-form-group>

      <b-form-row>
        <b-col md="6">
          <b-form-group label="From Program (anchor)" label-for="from_program" label-class="form-label">
            <v-select
              id="from_program"
              class="form-control v-select-custom"
              label="title"
              v-model="form.from_program"
              :reduce="p => p.id"
              placeholder="e.g. CNA"
              :options="programOptions"
              :loading="areProgramsLoading"
              @input="onFromProgramChange"
            >
              <template #search="{ attributes, events }">
                <input class="vs__search" :required="!form.from_program" v-bind="attributes" v-on="events" />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="To Program (degree or advanced certificate)" label-for="to_program" label-class="form-label">
            <v-select
              id="to_program"
              class="form-control v-select-custom"
              label="title"
              v-model="form.to_program"
              :reduce="p => p.id"
              placeholder="e.g. Nursing BSN"
              :options="degreeProgramOptions"
              :loading="areDegreeProgramsLoading"
              @input="onToProgramChange"
            >
              <template #search="{ attributes, events }">
                <input class="vs__search" :required="!form.to_program" v-bind="attributes" v-on="events" />
              </template>
            </v-select>
            <small class="text-muted">Only degree and advanced certificate programs can be selected.</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Midpoint (optional)" label-for="mid_program" label-class="form-label">
            <v-select
              id="mid_program"
              class="form-control v-select-custom"
              label="title"
              v-model="form.mid_program"
              :reduce="p => p.id"
              placeholder="e.g. CNA between CNA Prep and ADN"
              :options="midProgramOptions"
              :loading="areProgramsLoading"
              @input="onFromProgramChange(form.from_program)"
            />
            <small class="text-muted">Pre-health, certificate or advanced certificate. Earned at the entry school, so entry schools must offer it too.</small>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="State" label-for="state" label-class="form-label">
        <b-form-input
          id="state"
          placeholder="e.g. Illinois"
          v-model="form.state"
        />
      </b-form-group>

      <b-form-row>
        <b-col md="4">
          <b-form-group label="Timeframe" label-for="timeframe_value" label-class="form-label">
            <b-form-input
              id="timeframe_value"
              type="number"
              min="0"
              placeholder="e.g. 12"
              v-model.number="form.timeframe_value"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="To (optional)" label-for="timeframe_value_to" label-class="form-label">
            <b-form-input id="timeframe_value_to" type="number" min="0" placeholder="e.g. 18" v-model.number="form.timeframe_value_to" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Timeframe Unit" label-for="timeframe_unit" label-class="form-label">
            <v-select
              id="timeframe_unit"
              class="form-control v-select-custom"
              label="text"
              v-model="form.timeframe_unit"
              :reduce="u => u.value"
              placeholder="Select Unit"
              :options="timeframeUnitOptions"
            />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col md="6">
          <b-form-group label="Typical cost (USD)" label-for="cost_from" label-class="form-label" description="Total for the whole path. Leave empty if unknown.">
            <b-form-input id="cost_from" type="number" min="0" step="100" placeholder="e.g. 4500" v-model.number="form.cost_from" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Cost to (optional)" label-for="cost_to" label-class="form-label">
            <b-form-input id="cost_to" type="number" min="0" step="100" placeholder="e.g. 9000" v-model.number="form.cost_to" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="Supported language (besides English)" label-for="languages" label-class="form-label" description="One per path.">
        <v-select id="languages" class="form-control v-select-custom" label="text" v-model="language" :reduce="l => l.value" placeholder="Select a language" :options="languageOptions" />
      </b-form-group>

      <b-form-group label="Cover image" label-for="pathway_image" label-class="form-label" description="Shown on path cards and the path page. Landscape, at least 1200px wide.">
        <div class="d-flex align-items-start" style="gap: 16px">
          <img v-if="form.image_url" :src="form.image_url" alt="" style="width: 160px; height: 100px; object-fit: cover; border-radius: 10px; border: 1px solid #e5e7eb" />
          <div class="flex-grow-1">
            <b-form-file id="pathway_image" v-model="imageFile" accept="image/*" placeholder="Choose an image" :disabled="isImageUploading" @input="uploadImage" />
            <b-progress v-if="isImageUploading" :value="imagePercent" :max="100" class="mt-2" height="6px" />
            <b-form-input class="mt-2" type="url" placeholder="or paste an image URL" v-model.trim="form.image_url" />
          </div>
        </div>
      </b-form-group>

      <b-form-group label="Short Description" label-for="description" label-class="form-label">
        <b-form-textarea
          id="description"
          placeholder="Brief summary shown on the pathway card"
          :rows="3"
          v-model="form.description"
        />
      </b-form-group>

      <b-form-group label="Full Description" label-for="long_description" label-class="form-label">
        <b-form-textarea
          id="long_description"
          placeholder="Detailed description shown on the pathway detail page"
          :rows="6"
          v-model="form.long_description"
        />
      </b-form-group>

      <b-form-group label="Participating Schools" label-for="schools" label-class="form-label">
        <v-select
          id="schools"
          class="form-control v-select-custom"
          label="name"
          v-model="form.schools"
          :reduce="s => s.id"
          multiple
          :placeholder="form.from_program ? 'Select Schools' : 'Pick a From Program first'"
          :options="schoolOptions"
          :loading="areSchoolsLoading"
          :disabled="!form.from_program"
        />
        <small class="text-muted">
          Only schools participating in TransitionU paths that offer the selected From Program. Turn a school on in Edit School to add it here.
        </small>
      </b-form-group>

      <b-form-group label="University Programs" label-for="uni_offered_programs" label-class="form-label">
        <v-select
          id="uni_offered_programs"
          class="form-control v-select-custom"
          label="display_label"
          v-model="form.uni_offered_programs"
          :reduce="u => u.id"
          multiple
          :placeholder="form.to_program ? 'Select universities offering this program' : 'Pick a To Program first'"
          :options="uniOfferedOptions"
          :loading="areUniOfferedLoading"
          :disabled="!form.to_program"
        />
        <small class="text-muted">
          Filtered to universities offering the selected To Program.
        </small>
      </b-form-group>

      <!-- Creator: the TransitionU creator this path belongs to, and what they earn per enrollment -->
      <h5 class="pw-section">Creator</h5>
      <b-form-row>
        <b-col md="8">
          <b-form-group label="Creator" label-for="advisor" label-class="form-label">
            <v-select id="advisor" class="form-control v-select-custom" label="display_name" v-model="form.advisor" :reduce="a => a.id"
              placeholder="No creator (built by TransitionU)" :options="creatorOptions" :loading="areCreatorsLoading" />
            <small class="text-muted">TransitionU creators only. Enrollments on this path are credited to them.</small>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Payout per enrollment ($)" label-for="advisor_payout" label-class="form-label">
            <b-form-input id="advisor_payout" type="number" min="0" step="1" v-model="advisorPayoutDollars" :disabled="!form.advisor" placeholder="25" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <!-- Who this path is for -->
      <h5 class="pw-section">Who this path is for</h5>
      <b-form-row>
        <b-col md="6">
          <b-form-group label="Must already hold" label-for="required_prior_programs" label-class="form-label">
            <v-select id="required_prior_programs" class="form-control v-select-custom" label="title" v-model="form.required_prior_programs" :reduce="p => p.id" multiple
              placeholder="Credentials needed before the entry program" :options="programOptions" :loading="areProgramsLoading" />
            <small class="text-muted">The From Program is what they enroll into, so list only what they need before it. Medication Aide to LPN in Georgia needs a CNA; CNA to ADN needs nothing.</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Already past this path" label-for="excluded_prior_programs" label-class="form-label">
            <v-select id="excluded_prior_programs" class="form-control v-select-custom" label="title" v-model="form.excluded_prior_programs" :reduce="p => p.id" multiple
              placeholder="Programs that mean this path is behind them" :options="programOptions" :loading="areProgramsLoading" />
            <small class="text-muted">For CNA to ADN, pick ADN and BSN.</small>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="Before you start" label-class="form-label">
        <small class="d-block text-muted mb-2">Shown on the path page and asked as yes or no questions at enrollment.</small>
        <b-form-checkbox-group v-model="form.prerequisites" :options="PATHWAY_PREREQUISITES" stacked class="pw-prereqs" />
      </b-form-group>
      <b-form-group label="Anything else they need" label-for="prerequisites_note" label-class="form-label">
        <b-form-textarea id="prerequisites_note" :rows="2" v-model="form.prerequisites_note" placeholder="e.g. A current CNA certificate from any state." />
      </b-form-group>

      <!-- FAQ -->
      <div class="pw-section d-flex align-items-center justify-content-between">
        <h5 class="mb-0">FAQ</h5>
        <b-btn variant="outline-primary" size="sm" @click.prevent="form.faqs.push({ question: '', answer: '', order: form.faqs.length })"><i class="fas fa-plus mr-1"></i>Add question</b-btn>
      </div>
      <div v-for="(faq, index) in form.faqs" :key="`faq-${index}`" class="pw-faq">
        <div class="pw-faq__num">{{ index + 1 }}</div>
        <div class="pw-faq__body">
          <b-form-input v-model.trim="faq.question" placeholder="Question" class="mb-2" />
          <b-form-textarea v-model.trim="faq.answer" :rows="2" placeholder="Answer, in plain words" />
        </div>
        <div class="pw-faq__actions">
          <b-btn variant="light" size="sm" title="Move up" :disabled="index === 0" @click.prevent="moveFaq(index, -1)"><i class="fas fa-arrow-up"></i></b-btn>
          <b-btn variant="light" size="sm" title="Move down" :disabled="index === form.faqs.length - 1" @click.prevent="moveFaq(index, 1)"><i class="fas fa-arrow-down"></i></b-btn>
          <b-btn variant="light" size="sm" title="Remove" @click.prevent="form.faqs.splice(index, 1)"><i class="fas fa-trash text-danger"></i></b-btn>
        </div>
      </div>
      <p v-if="!form.faqs.length" class="text-muted font-italic small">No questions yet. Paths without a FAQ show a generated one on the site.</p>

      <h5 class="pw-section">Status</h5>
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="form.is_active" name="is-active-switch" switch>
          Active?
        </b-form-checkbox>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions } from 'vuex';
import { PATHWAY_PREREQUISITES } from '@/common/constants';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';

export default {
  components: { PageHeader },
  name: 'PostPathway',
  extends: Page,

  data() {
    return {
      isLoading: false,
      areProgramsLoading: false,
      areDegreeProgramsLoading: false,
      areSchoolsLoading: false,
      areUniOfferedLoading: false,
      programOptions: [],
      midProgramOptions: [],
      degreeProgramOptions: [],
      schoolOptions: [],
      uniOfferedOptions: [],
      timeframeUnitOptions: [
        { text: 'Months', value: 'months' },
        { text: 'Years', value: 'years' },
      ],
      // Same order as the backend PATHWAY_LANGUAGES: most spoken in US homes first.
      languageOptions: [
        ['es', 'Spanish'], ['zh', 'Chinese'], ['tl', 'Tagalog'], ['vi', 'Vietnamese'], ['ar', 'Arabic'], ['fr', 'French'], ['ko', 'Korean'], ['ru', 'Russian'],
        ['pt', 'Portuguese'], ['ht', 'Haitian Creole'], ['hi', 'Hindi'], ['de', 'German'], ['pl', 'Polish'], ['it', 'Italian'], ['ur', 'Urdu'], ['ja', 'Japanese'],
        ['fa', 'Persian'], ['gu', 'Gujarati'], ['te', 'Telugu'], ['bn', 'Bengali'], ['ta', 'Tamil'], ['pa', 'Punjabi'], ['am', 'Amharic'], ['so', 'Somali'], ['hmn', 'Hmong'],
      ].map(([value, text]) => ({ value, text })),
      imageFile: null,
      isImageUploading: false,
      imagePercent: 0,
      form: {
        title: '',
        subtitle: '',
        from_program: null,
        mid_program: null,
        to_program: null,
        state: '',
        timeframe_value: null,
        timeframe_value_to: null,
        timeframe_unit: null,
        cost_from: null,
        cost_to: null,
        languages: [],
        image_url: '',
        description: '',
        long_description: '',
        schools: [],
        uni_offered_programs: [],
        is_active: true,
        advisor: null,
        advisor_payout_cents: 0,
        required_prior_programs: [],
        excluded_prior_programs: [],
        prerequisites: [],
        prerequisites_note: '',
        faqs: [],
      },
      creatorOptions: [],
      areCreatorsLoading: false,
      PATHWAY_PREREQUISITES,
    };
  },

  computed: {
    language: {
      get() { return (this.form.languages && this.form.languages[0]) || null; },
      set(v) { this.form.languages = v ? [v] : []; },
    },
    advisorPayoutDollars: {
      get() { return this.form.advisor_payout_cents ? Math.round(this.form.advisor_payout_cents / 100) : ''; },
      set(v) { this.form.advisor_payout_cents = v ? Math.round(Number(v) * 100) : 0; },
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getPathway', 'createPathway', 'updatePathway']),
    ...mapActions('uni', ['getUniImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),

    async uploadImage(file) {
      if (!file) return;
      this.isImageUploading = true;
      this.imagePercent = 0;
      try {
        const urlResp = await this.getUniImageUploadPresignedUrl({ file_name: file.name, content_type: file.type, upload_type: 'pathways' });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file,
          config: { onUploadProgress: e => { this.imagePercent = Math.round((e.loaded * 100) / e.total); } },
        });
        this.form.image_url = urlResp.upload_url.split('?')[0];
        this.makeToast({ variant: 'success', msg: 'Image uploaded.' });
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: 'Upload failed. Try again or paste a URL.' });
      }
      this.isImageUploading = false;
      this.imageFile = null;
    },
    ...mapActions('advisors', ['getAllAdvisors']),
    moveFaq(index, dir) {
      const target = index + dir;
      if (target < 0 || target >= this.form.faqs.length) return;
      const rows = [...this.form.faqs];
      [rows[index], rows[target]] = [rows[target], rows[index]];
      this.form.faqs = rows.map((f, i) => ({ ...f, order: i }));
    },
    async fetchCreators() {
      this.areCreatorsLoading = true;
      try {
        const response = await this.getAllAdvisors({ limit: 500, is_transitionu_creator: true });
        this.creatorOptions = get(response, 'data.results', get(response, 'data', [])).map(a => ({
          ...a, display_name: `${a.first_name} ${a.last_name}${a.transitionu_handle ? ` (@${a.transitionu_handle})` : ''}`,
        }));
      } catch (_err) {
        this.creatorOptions = [];
      }
      this.areCreatorsLoading = false;
    },
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('school', ['getAllSchools']),
    ...mapActions('uni', ['getAllUniOfferedPrograms']),

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const response = await this.getAllPrograms({ limit: 300, prg_type__in: 'prep_course,pre_health,certificate,advanced_certificate,continuing_education' });
        this.programOptions = get(response, 'data.results', []);
        this.midProgramOptions = this.programOptions.filter(p => ['pre_health', 'certificate', 'advanced_certificate'].includes(p.prg_type));
      } catch (_err) {
        this.programOptions = [];
      }
      this.areProgramsLoading = false;
    },

    async fetchDegreePrograms() {
      this.areDegreeProgramsLoading = true;
      try {
        const response = await this.getAllPrograms({ limit: 200, prg_type__in: 'degree,advanced_certificate' });
        this.degreeProgramOptions = get(response, 'data.results', []);
      } catch (_err) {
        this.degreeProgramOptions = [];
      }
      this.areDegreeProgramsLoading = false;
    },

    async fetchSchools(fromProgramId) {
      if (!fromProgramId) {
        this.schoolOptions = [];
        return;
      }
      this.areSchoolsLoading = true;
      try {
        const response = await this.getAllSchools({
          limit: 500,
          offered_programs__id: fromProgramId,
          transitionu_paths_enabled: true,
        });
        let schools = get(response, 'data.results', []);
        if (this.form.mid_program) {
          // Entry schools must offer the midpoint too (students earn it at the same school).
          const mid = await this.getAllSchools({ limit: 500, offered_programs__id: this.form.mid_program, transitionu_paths_enabled: true });
          const midIds = new Set(get(mid, 'data.results', []).map(sc => sc.id));
          schools = schools.filter(sc => midIds.has(sc.id));
        }
        this.schoolOptions = schools;
      } catch (_err) {
        this.schoolOptions = [];
      }
      this.areSchoolsLoading = false;
    },

    onFromProgramChange(newProgramId) {
      const previousIds = this.form.schools;
      this.fetchSchools(newProgramId).then(() => {
        const allowed = new Set(this.schoolOptions.map(s => s.id));
        this.form.schools = previousIds.filter(id => allowed.has(id));
      });
    },

    async fetchUniOfferedPrograms(programId) {
      if (!programId) {
        this.uniOfferedOptions = [];
        return;
      }
      this.areUniOfferedLoading = true;
      try {
        const response = await this.getAllUniOfferedPrograms({ limit: 500, program: programId });
        const results = get(response, 'data.results', []);
        this.uniOfferedOptions = results.map(uop => ({
          ...uop,
          display_label: `${get(uop, 'university.name', '—')} — ${get(uop, 'program.title', '—')}`,
        }));
      } catch (_err) {
        this.uniOfferedOptions = [];
      }
      this.areUniOfferedLoading = false;
    },

    onToProgramChange(newProgramId) {
      const previousIds = this.form.uni_offered_programs;
      this.fetchUniOfferedPrograms(newProgramId).then(() => {
        const allowed = new Set(this.uniOfferedOptions.map(o => o.id));
        this.form.uni_offered_programs = previousIds.filter(id => allowed.has(id));
      });
    },

    async loadPathway(id) {
      try {
        const response = await this.getPathway(id);
        const data = get(response, 'data', {});
        const fromProgramId = get(data, 'from_program.id', null);
        const toProgramId = get(data, 'to_program.id', null);
        this.form = {
          title: data.title || '',
          subtitle: data.subtitle || '',
          from_program: fromProgramId,
          mid_program: get(data, 'mid_program.id', null),
          to_program: toProgramId,
          state: data.state || '',
          timeframe_value: data.timeframe_value || null,
          timeframe_value_to: data.timeframe_value_to || null,
          timeframe_unit: data.timeframe_unit || null,
          cost_from: data.cost_from || null,
          cost_to: data.cost_to || null,
          languages: data.languages || [],
          image_url: data.image_url || '',
          description: data.description || '',
          long_description: data.long_description || '',
          schools: (data.schools || []).map(s => s.id),
          uni_offered_programs: (data.uni_offered_programs || []).map(u => u.id),
          is_active: data.is_active !== false,
          advisor: get(data, 'advisor.id', null),
          advisor_payout_cents: data.advisor_payout_cents || 0,
          required_prior_programs: (data.required_prior_programs || []).map(p => p.id),
          excluded_prior_programs: (data.excluded_prior_programs || []).map(p => p.id),
          prerequisites: (data.prerequisites || []).map(p => (typeof p === 'string' ? p : p.key)),
          prerequisites_note: data.prerequisites_note || '',
          faqs: (data.faqs || []).map((f, i) => ({ id: f.id, question: f.question, answer: f.answer, order: i })),
        };
        await Promise.all([
          fromProgramId ? this.fetchSchools(fromProgramId) : Promise.resolve(),
          toProgramId ? this.fetchUniOfferedPrograms(toProgramId) : Promise.resolve(),
        ]);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const payload = { ...this.form };
        payload.faqs = this.form.faqs.filter(f => f.question && f.answer).map((f, i) => ({ ...f, order: i }));
        if (!payload.advisor) payload.advisor_payout_cents = 0;
        if (!payload.timeframe_value) payload.timeframe_value = null;
        if (!payload.timeframe_value_to) payload.timeframe_value_to = null;
        if (!payload.timeframe_unit) payload.timeframe_unit = null;
        if (!payload.cost_from) payload.cost_from = null;
        if (!payload.cost_to) payload.cost_to = null;
        if (!payload.image_url) payload.image_url = null;

        if (this.$route.params.id) {
          await this.updatePathway({ id: this.$route.params.id, data: payload });
          this.makeToast({ variant: 'success', msg: 'Pathway updated.' });
        } else {
          await this.createPathway(payload);
          this.makeToast({ variant: 'success', msg: 'Pathway created.' });
        }
        this.$router.push({ name: 'sa-manage-pathways' });
      } catch (err) {
        const detail = get(err, 'response.data.detail');
        const fieldErrors = get(err, 'response.data');
        let msg = this.$t('generalMsgs.genErrorMsg');
        if (detail) {
          msg = detail;
        } else if (fieldErrors && typeof fieldErrors === 'object') {
          const firstKey = Object.keys(fieldErrors)[0];
          const firstVal = fieldErrors[firstKey];
          msg = Array.isArray(firstVal) ? firstVal[0] : String(firstVal);
        }
        this.makeToast({ variant: 'danger', msg });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    await Promise.all([this.fetchPrograms(), this.fetchDegreePrograms(), this.fetchCreators()]);
    if (this.$route.params.id) {
      await this.loadPathway(this.$route.params.id);
    }
  },
};
</script>

<style lang="scss" scoped>
.pw-section { margin: 26px 0 12px; padding-bottom: 8px; border-bottom: 1px solid #e9ecef; font-size: 15px; font-weight: 700; }
.pw-section h5 { font-size: 15px; font-weight: 700; }
.pw-prereqs ::v-deep .custom-control { margin-bottom: 4px; }
.pw-faq { display: flex; gap: 12px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #f1f3f5; }
.pw-faq__num { flex: none; width: 28px; height: 28px; border-radius: 50%; background: #245fcc; color: #fff; display: grid; place-items: center; font-size: 13px; font-weight: 700; margin-top: 4px; }
.pw-faq__body { flex: 1; min-width: 0; }
.pw-faq__actions { flex: none; display: flex; gap: 4px; }
</style>
